'use client'

import { motion } from 'framer-motion'
import Link from 'next/link'
import { ArrowRight, Check, MessageSquareReply, Star, TrendingUp, Bell } from 'lucide-react'
import { useTranslations } from 'next-intl'
import '../../sass/landing/ReviewsSection.scss'

const reviewBullets = ['b1', 'b2', 'b3', 'b4']

const GoogleMark = () => (
  <svg className='reviews-section__gmark' viewBox='0 0 24 24' aria-hidden='true'>
    <path d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z' fill='#4285F4' />
    <path d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z' fill='#34A853' />
    <path d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z' fill='#FBBC05' />
    <path d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z' fill='#EA4335' />
  </svg>
)

const ReviewsSection = () => {
  const t = useTranslations('LandingV2.reviews')

  return (
    <section id='reviews' className='reviews-section'>
      <div className='reviews-section__container'>
        <motion.div
          className='reviews-section__header'
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5 }}
        >
          <span className='landing-v2__chip'>{t('chip')}</span>
          <h2>{t('heading')}</h2>
          <p>{t('description')}</p>
        </motion.div>

        <motion.div
          className='reviews-section__grid'
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className='reviews-section__text'>
            <span className='reviews-section__eyebrow'>
              <Star size={14} /> {t('eyebrow')}
            </span>
            <ul className='reviews-section__list'>
              {reviewBullets.map((id) => (
                <li key={id} className='reviews-section__list-item'>
                  <Check size={16} /> {t(`bullets.${id}`)}
                </li>
              ))}
            </ul>
            <Link href='/login/signup' className='landing-v2__btn landing-v2__btn--accent reviews-section__cta'>
              {t('cta')} <ArrowRight size={15} />
            </Link>
          </div>

          <div className='reviews-section__visual'>
            <div className='reviews-section__visual-glow' />

            <div className='reviews-section__summary'>
              <div className='reviews-section__score'>
                <strong>{t('summaryScore')}</strong>
                <span className='reviews-section__stars' aria-hidden='true'>
                  {Array.from({ length: 5 }, (_, index) => (
                    <Star key={index} className={index < 5 ? 'is-filled' : ''} />
                  ))}
                </span>
                <span className='reviews-section__summary-label'>{t('summaryLabel')}</span>
              </div>
              <div className='reviews-section__brand'>
                <GoogleMark />
                <span>{t('summarySource')}</span>
              </div>
            </div>

            <article className='reviews-section__review'>
              <div className='reviews-section__author'>
                <span className='reviews-section__avatar'>M</span>
                <div>
                  <strong>{t('reviewAuthor')}</strong>
                  <span className='reviews-section__review-stars' aria-hidden='true'>
                    {Array.from({ length: 5 }, (_, index) => (
                      <Star key={index} className='is-filled' />
                    ))}
                  </span>
                </div>
                <span className='reviews-section__date'>{t('reviewDate')}</span>
              </div>
              <p className='reviews-section__comment'>{t('reviewComment')}</p>
              <div className='reviews-section__reply'>
                <MessageSquareReply size={14} />
                <span>{t('reviewReply')}</span>
              </div>
            </article>

            <div className='reviews-section__chips'>
              <span className='reviews-section__chip'>
                <TrendingUp size={14} /> {t('chipInsights')}
              </span>
              <span className='reviews-section__chip'>
                <Bell size={14} /> {t('chipAlerts')}
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default ReviewsSection
